import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import FlashInfo from '../FlashInfo';
import { 
  Smartphone, 
  Wrench, 
  Clock, 
  MapPin, 
  Phone, 
  Mail,
  Star,
  ShoppingBag,
  Calendar,
  CheckCircle,
  Award,
  Zap,
  Shield,
  Settings
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [reviews, setReviews] = useState([]);
  const [shopSettings, setShopSettings] = useState(null);

  useEffect(() => {
    fetchShopSettings();
    fetchReviews();
  }, []);

  const fetchShopSettings = async () => {
    try {
      const response = await axios.get(`${API}/shop-settings`);
      setShopSettings(response.data);
    } catch (error) {
      console.error('Error fetching shop settings:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/google`);
      if (response.data.reviews) {
        setReviews(response.data.reviews);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const services = [
    {
      icon: Smartphone,
      title: "Réparation Smartphones",
      description: "Écran cassé, batterie, connecteur de charge, problèmes logiciels",
      color: "blue",
      price: "À partir de 29€"
    },
    {
      icon: Wrench,
      title: "Réparation PC, Tablettes & Consoles",
      description: "Diagnostic complet, réparation matérielle et logicielle - PC, iPad, PlayStation, Xbox, Nintendo Switch",
      color: "purple",
      price: "À partir de 39€"
    },
    {
      icon: ShoppingBag,
      title: "Boutique & Promotions",
      description: "Smartphones neufs/reconditionnés, PC portables, accessoires et abonnements",
      color: "pink",
      price: "Voir nos offres",
      link: "/public/promotions",
      badge: "Nouveautés"
    },
    {
      icon: Zap,
      title: "Abonnements Internet, TV & Mobile",
      description: "VOO, DIGI-BELGIUM, Hey Télécom - Meilleurs tarifs",
      color: "orange",
      price: "Comparez & économisez",
      link: "/public/promotions?category=abonnements",
      badge: "Offres spéciales"
    }
  ];

  const advantages = [
    {
      icon: Clock,
      title: "Réparation Rapide",
      description: "La plupart des réparations en moins de 1h"
    },
    {
      icon: Shield,
      title: "Garantie 3 mois",
      description: "Sur toutes les réparations et pièces"
    },
    {
      icon: Award,
      title: "Expertise",
      description: "Techniciens certifiés et expérimentés"
    },
    {
      icon: CheckCircle,
      title: "Diagnostic Gratuit",
      description: "Évaluation sans engagement"
    }
  ];

  const getOpeningHours = () => {
    if (!shopSettings || !shopSettings.business_hours) {
      return [
        { day: "Lundi", hours: "9h00 - 18h00" },
        { day: "Mardi", hours: "9h00 - 18h00" },
        { day: "Mercredi", hours: "9h00 - 18h00" },
        { day: "Jeudi", hours: "9h00 - 18h00" },
        { day: "Vendredi", hours: "9h00 - 18h00" },
        { day: "Samedi", hours: "9h00 - 13h00" },
        { day: "Dimanche", hours: "Fermé", closed: true }
      ];
    }

    const days = [
      { key: 'monday', label: 'Lundi' },
      { key: 'tuesday', label: 'Mardi' },
      { key: 'wednesday', label: 'Mercredi' },
      { key: 'thursday', label: 'Jeudi' },
      { key: 'friday', label: 'Vendredi' },
      { key: 'saturday', label: 'Samedi' },
      { key: 'sunday', label: 'Dimanche' }
    ];

    return days.map(day => ({
      day: day.label,
      hours: shopSettings.business_hours[day.key] || 'Non défini',
      closed: shopSettings.business_hours[day.key]?.toLowerCase().includes('fermé') || false
    }));
  };

  const openingHours = getOpeningHours();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Top Navigation Bar */}
      <div className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="relative flex items-center justify-center">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-white" />
                </div>
                <Wrench className="w-4 h-4 text-white absolute -bottom-1 -right-1 bg-orange-500 rounded-full p-0.5" />
              </div>
              <span className="font-bold text-lg text-gray-900">
                {shopSettings?.shop_name || 'Smart Service Mobile'}
              </span>
            </div>
            <Link to="/login">
              <Button variant="outline" size="sm" className="flex items-center gap-2 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-600">
                <Settings className="w-4 h-4" />
                Espace Technicien
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-700 text-white">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="relative">
                <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center shadow-2xl">
                  <Smartphone className="w-10 h-10 text-blue-600" />
                  <Wrench className="w-8 h-8 text-orange-500 absolute -bottom-2 -right-2 bg-white rounded-full p-1 shadow-lg" />
                </div>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in">
              {shopSettings?.shop_name || 'Smart Service Mobile'}
            </h1>
            
            <p className="text-xl md:text-2xl mb-4 text-blue-100">
              Votre Expert en Réparation Mobile à Liège
            </p>
            
            <p className="text-lg mb-8 text-blue-50 max-w-2xl mx-auto">
              Réparation rapide, qualité garantie. Smartphones, tablettes, PC et vente d'appareils reconditionnés
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/public/appointment">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 shadow-xl">
                  <Calendar className="w-5 h-5 mr-2" />
                  Prendre Rendez-vous
                </Button>
              </Link>
              <Link to="/public/contact">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-600">
                  <Mail className="w-5 h-5 mr-2" />
                  Demander un Devis
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Wave decoration */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" 
                  fill="rgb(248, 250, 252)" />
          </svg>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Flash Info */}
        <FlashInfo pageName="homepage" />
        
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Nos Produits et Services
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Des solutions complètes pour tous vos besoins en téléphonie mobile
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            const colorClasses = {
              blue: 'from-blue-500 to-blue-600',
              purple: 'from-purple-500 to-purple-600',
              green: 'from-green-500 to-green-600',
              orange: 'from-orange-500 to-orange-600',
              pink: 'from-pink-500 to-rose-600'
            };
            
            return service.link ? (
              <Link key={index} to={service.link} className="block">
                <Card className="h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-none cursor-pointer">
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colorClasses[service.color]} flex items-center justify-center mb-4 shadow-lg`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-3 text-sm">
                      {service.description}
                    </p>
                    <p className="text-sm font-semibold text-blue-600">
                      {service.price}
                    </p>
                    {service.badge && (
                      <div className="mt-3">
                        <Badge className="bg-gradient-to-r from-pink-500 to-rose-600 text-white">
                          {service.badge}
                        </Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ) : (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-none">
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colorClasses[service.color]} flex items-center justify-center mb-4 shadow-lg`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-3 text-sm">
                    {service.description}
                  </p>
                  <p className="text-sm font-semibold text-blue-600">
                    {service.price}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi Nous Choisir ?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => {
              const Icon = advantage.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                    <Icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {advantage.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {advantage.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Avis de nos Clients
            </h2>
            <div className="flex items-center justify-center gap-2 mb-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-gray-600">Basé sur {reviews.length}+ avis Google</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.slice(0, 3).map((review, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(review.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 text-sm italic">
                    "{review.text?.substring(0, 150)}..."
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {review.author_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {review.relative_time_description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Contact & Hours Section */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold mb-6">Nous Contacter</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 mt-1 text-blue-400" />
                  <div>
                    <p className="font-semibold">Adresse</p>
                    <p className="text-gray-300">
                      {shopSettings?.address_street}<br />
                      {shopSettings?.address_zip} {shopSettings?.address_city}<br />
                      {shopSettings?.address_country}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 mt-1 text-blue-400" />
                  <div>
                    <p className="font-semibold">Téléphone</p>
                    <a href={`tel:${shopSettings?.phone_mobile}`} className="text-blue-400 hover:text-blue-300">
                      {shopSettings?.phone_mobile}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 mt-1 text-blue-400" />
                  <div>
                    <p className="font-semibold">Email</p>
                    <a href={`mailto:${shopSettings?.email}`} className="text-blue-400 hover:text-blue-300">
                      {shopSettings?.email}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div>
              <h2 className="text-2xl font-bold mb-6">Horaires d'Ouverture</h2>
              <div className="space-y-2">
                {openingHours.map((schedule, index) => (
                  <div key={index} className={`flex justify-between py-2 border-b border-gray-700 ${schedule.closed ? 'text-gray-500' : ''}`}>
                    <span className="font-medium">{schedule.day}</span>
                    <span>{schedule.hours}</span>
                  </div>
                ))}
              </div>
              
              {/* Exceptional Hours */}
              {shopSettings?.exceptional_hours && shopSettings.exceptional_hours.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <h3 className="text-lg font-semibold mb-3 text-yellow-400">Horaires Exceptionnels</h3>
                  <div className="space-y-2">
                    {shopSettings.exceptional_hours.map((exceptional, index) => (
                      <div key={index} className="bg-gray-800 bg-opacity-50 rounded-lg p-3 border border-yellow-500 border-opacity-30">
                        <div className="font-medium text-yellow-300">{exceptional.description}</div>
                        <div className="text-sm text-gray-300 mt-1">
                          {new Date(exceptional.date).toLocaleDateString('fr-FR', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </div>
                        <div className="text-sm text-white mt-1">{exceptional.hours}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Prêt à Réparer Votre Appareil ?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Prenez rendez-vous en ligne ou contactez-nous pour un devis gratuit
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/public/appointment">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50">
                <Calendar className="w-5 h-5 mr-2" />
                Prendre Rendez-vous
              </Button>
            </Link>
            <Link to="/public/promotions">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-600">
                <ShoppingBag className="w-5 h-5 mr-2" />
                Voir les Promotions
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
